// Session de pratique : appariement entre notes attendues et notes jouées, pour les
// deux modes (attente, tempo imposé). Le moteur ne dépend ni de React ni du navigateur :
// il reçoit des événements d'entrée horodatés et une horloge (ms).
//
// Règle absolue : une note attendue n'est validée que par un événement sûr dont la
// hauteur correspond. Une détection incertaine est signalée, jamais comptée.
//
// Le bilan sépare trois choses : la qualité du jeu sur la partie évaluable (justesse,
// rythme, précision), la proportion réellement évaluable (couverture : les notes dont
// la fenêtre n'a reçu que des détections incertaines ne sont pas jugées), et la nature
// de l'entrée (instrument ou clavier d'essai). La réussite exige les trois.

import { HANDS, buildSteps, msToTicks, ticksPerBeat, ticksPerMeasure, ticksToMs, totalTicks, type Hand, type Note, type Score, type Step } from '../music/model';
import { pitchClass } from '../music/pitchNames';
import type { InputEvent, InputKind } from './types';

export type Mode = 'wait' | 'tempo';

export interface SessionOptions {
  mode: Mode;
  hands: readonly Hand[];
  /** Tempo de la session, en noires par minute. */
  tempo: number;
  /** Tolérance (ms) pour juger une note en rythme. */
  toleranceMs: number;
  /** Fenêtre large (ms) : au-delà, une note attendue est manquée et une note jouée n'est plus rattachée. */
  wideMs: number;
  /** Décompte ou avance visuelle avant la première note, en mesures. */
  leadInMeasures: number;
  /** Mode attente : une note jouée un peu avant l'arrivée du pas est acceptée dans ce délai. */
  earlyAcceptMs: number;
  /**
   * Délai maximal (ms) entre l'instant d'une frappe et la réception de son événement
   * (compensation de latence, traitement). Une note attendue n'est déclarée manquée
   * qu'après wideMs + inputDelayMs, pour laisser arriver les événements retardés.
   */
  inputDelayMs: number;
  /** Après avoir été déclarée manquée, une note reste rattrapable par un événement retardé pendant ce délai (ms). */
  reconcileMs: number;
}

export const DEFAULT_SESSION_OPTIONS: SessionOptions = {
  mode: 'wait',
  hands: HANDS,
  tempo: 72,
  toleranceMs: 120,
  wideMs: 300,
  leadInMeasures: 1,
  earlyAcceptMs: 400,
  inputDelayMs: 150,
  reconcileMs: 1500,
};

export type ResultCategory = 'correct' | 'early' | 'late' | 'wrong' | 'wrongOctave' | 'extra' | 'missed' | 'uncertain';

export interface NoteResult {
  category: ResultCategory;
  time: number;
  /** Hauteur jouée (null pour une attaque sans hauteur ou une note non jouée). */
  pitch: number | null;
  /** Hauteur attendue concernée, si elle existe. */
  expectedPitch?: number;
  noteId?: string;
  /** Écart de temps (ms) par rapport à l'instant attendu, mode tempo. */
  deltaMs?: number;
  /** Ce résultat corrige un résultat provisoire : la note avait été déclarée manquée, un événement retardé l'a rattachée. */
  revised?: boolean;
}

export type SessionStatus = 'idle' | 'leadIn' | 'running' | 'waiting' | 'paused' | 'ended';

export interface SessionSummary {
  mode: Mode;
  tempo: number;
  /** Mains travaillées pendant la session. */
  hands: Hand[];
  /** Vrai si toutes les mains du morceau étaient travaillées. */
  fullHands: boolean;
  /** Sources d'entrée ayant produit au moins une note pendant la session. */
  sources: InputKind[];
  /** Vrai si toutes les notes viennent d'un instrument (micro ou MIDI), jamais du clavier d'essai ni de la synthèse. */
  instrument: boolean;
  /** Notes attendues. */
  expected: number;
  /** Notes attendues réellement évaluables (fenêtre sans détection incertaine non résolue). */
  evaluated: number;
  /** Notes attendues non évaluables : captation insuffisante. */
  unevaluated: number;
  correct: number;
  early: number;
  late: number;
  /** Notes attendues jouées avec la bonne hauteur : correct + early + late. */
  hits: number;
  wrong: number;
  wrongOctave: number;
  extra: number;
  /** Notes évaluables non jouées. */
  missed: number;
  /** Détections incertaines reçues (jamais comptées comme justes). */
  uncertainEvents: number;
  steps: number;
  firstTrySteps: number;
  /** Justesse sur la partie évaluable : hits / evaluated (mode attente : pas réussis du premier coup / pas). */
  accuracyPitch: number;
  /** Rythme sur la partie évaluable : correct / evaluated (mode attente : identique à accuracyPitch). */
  accuracyTiming: number;
  /** Précision : part des notes jouées qui étaient attendues, hits / (hits + fausses + mauvaise octave + en trop). */
  precision: number;
  /** Couverture : evaluated / expected (mode attente : part des frappes sûres parmi les frappes reçues). */
  coverage: number;
  /** Score global : hits / (evaluated + fausses + mauvaise octave + en trop) ; mode attente : accuracyPitch. */
  quality: number;
}

export type SessionEvent =
  | { type: 'feedback'; result: NoteResult }
  | { type: 'beat'; beat: number; measure: number; countIn: boolean; time: number }
  | { type: 'status'; status: SessionStatus; reason?: string }
  | { type: 'step'; stepIndex: number }
  | { type: 'end'; summary: SessionSummary };

interface ExpectedNote {
  note: Note;
  /** Instant attendu, en ms depuis le tick 0. */
  offsetMs: number;
  status: 'pending' | 'hit' | 'missed' | 'uncertain';
  category?: ResultCategory;
  /** Instant (horloge de session) auquel la note a été déclarée manquée ou non évaluable. */
  finalizedAt?: number;
}

export type NoteState = ResultCategory | 'pending';

export class PracticeSession {
  readonly score: Score;
  readonly options: SessionOptions;
  readonly steps: Step[];
  private readonly expected: ExpectedNote[];
  private readonly byId = new Map<string, ExpectedNote>();
  private readonly tpb: number;
  private readonly leadInTicks: number;
  private readonly endTick: number;

  status: SessionStatus = 'idle';
  private statusBeforePause: SessionStatus = 'idle';
  private pausedAt = 0;
  private lastNow = 0;
  private lastBeatIndex: number | null = null;
  private readonly sources = new Set<InputKind>();

  // Mode tempo : instant (ms) auquel le tick vaut -leadInTicks.
  private startTime = 0;
  private uncertainLog: { pitch: number | null; time: number }[] = [];

  // Mode attente.
  private stepIndex = -1;
  private pending = new Set<number>();
  private stepHadError = false;
  private firstTrySteps = 0;
  private anchorTick = 0;
  private anchorTime = 0;
  private targetTick: number | null = null;
  private targetStep: number | null = null;

  private counts = { wrong: 0, wrongOctave: 0, extra: 0, uncertainEvents: 0 };
  private summary: SessionSummary | null = null;

  constructor(score: Score, options: Partial<SessionOptions> = {}) {
    this.score = score;
    this.options = { ...DEFAULT_SESSION_OPTIONS, ...options };
    this.steps = buildSteps(score, this.options.hands);
    this.tpb = ticksPerBeat(score);
    this.leadInTicks = ticksPerMeasure(score) * this.options.leadInMeasures;
    this.endTick = totalTicks(score, this.options.hands);
    this.expected = this.steps.flatMap((s) =>
      s.notes.map((note) => ({ note, offsetMs: ticksToMs(note.start, this.options.tempo, score.ppq), status: 'pending' as const })),
    );
    for (const e of this.expected) this.byId.set(e.note.id, e);
  }

  private currentStatus(): SessionStatus {
    return this.status;
  }

  get mode(): Mode {
    return this.options.mode;
  }

  get currentStepIndex(): number {
    return this.stepIndex;
  }

  get pendingPitches(): number[] {
    return [...this.pending].sort((a, b) => a - b);
  }

  get expectedCount(): number {
    return this.expected.length;
  }

  get hitCount(): number {
    return this.expected.filter((e) => e.status === 'hit').length;
  }

  /** Mains du morceau (toutes ses parties). */
  get scoreHands(): Hand[] {
    return this.score.parts.map((p) => p.hand);
  }

  /** État de chaque note attendue, pour l'affichage. */
  noteStates(): Map<string, NoteState> {
    const map = new Map<string, NoteState>();
    for (const e of this.expected) map.set(e.note.id, e.category ?? 'pending');
    return map;
  }

  getSummary(): SessionSummary | null {
    return this.summary;
  }

  /** Durée totale (ms) de la session en mode tempo, décompte inclus. */
  get totalDurationMs(): number {
    return ticksToMs(this.leadInTicks + this.endTick, this.options.tempo, this.score.ppq);
  }

  start(now: number): SessionEvent[] {
    if (this.status !== 'idle') return [];
    this.lastNow = now;
    const events: SessionEvent[] = [];
    if (this.options.mode === 'tempo') {
      this.startTime = now;
      this.status = 'leadIn';
      events.push({ type: 'status', status: this.status });
    } else {
      this.anchorTick = -this.leadInTicks;
      this.anchorTime = now;
      this.status = 'leadIn';
      events.push({ type: 'status', status: this.status });
      this.beginGlide(0, now);
    }
    return events;
  }

  stop(): SessionEvent[] {
    if (this.status === 'idle' || this.status === 'ended') return [];
    return this.finish(this.lastNow);
  }

  /** Position de lecture en ticks (fractionnaire) à l'instant donné. Négatif pendant le décompte. */
  tickAt(now: number): number {
    switch (this.status) {
      case 'idle':
        return -this.leadInTicks;
      case 'paused':
        return this.tickAtInternal(this.pausedAt);
      case 'ended':
        return this.options.mode === 'tempo' ? Math.min(this.tickAtInternal(now), this.endTick) : this.anchorTick;
      default:
        return this.tickAtInternal(now);
    }
  }

  private tickAtInternal(now: number): number {
    if (this.options.mode === 'tempo') {
      return msToTicks(now - this.startTime, this.options.tempo, this.score.ppq) - this.leadInTicks;
    }
    if (this.targetTick === null) return this.anchorTick;
    return Math.min(this.targetTick, this.anchorTick + msToTicks(now - this.anchorTime, this.options.tempo, this.score.ppq));
  }

  /** Instant absolu (ms) auquel un tick est atteint en mode tempo. */
  timeOfTick(tick: number): number {
    return this.startTime + ticksToMs(tick + this.leadInTicks, this.options.tempo, this.score.ppq);
  }

  pause(now: number, reason?: string): SessionEvent[] {
    if (this.status === 'idle' || this.status === 'ended' || this.status === 'paused') return [];
    this.statusBeforePause = this.status;
    this.pausedAt = now;
    this.status = 'paused';
    return [{ type: 'status', status: 'paused', reason }];
  }

  resume(now: number): SessionEvent[] {
    if (this.status !== 'paused') return [];
    const delta = now - this.pausedAt;
    this.startTime += delta;
    this.anchorTime += delta;
    for (const u of this.uncertainLog) u.time += delta;
    for (const e of this.expected) if (e.finalizedAt !== undefined) e.finalizedAt += delta;
    this.status = this.statusBeforePause;
    this.lastNow = now;
    return [{ type: 'status', status: this.status }];
  }

  /** À appeler à chaque image : fait avancer la session et produit les événements de temps. */
  update(now: number): SessionEvent[] {
    if (this.status === 'idle' || this.status === 'ended' || this.status === 'paused') return [];
    if (now < this.lastNow) now = this.lastNow;
    this.lastNow = now;
    const events: SessionEvent[] = [];
    // Les temps sont émis avant le changement d'état, pour que le temps d'arrivée sur un pas soit compté.
    if (this.currentStatus() !== 'waiting') this.emitBeat(now, events);
    if (this.options.mode === 'tempo') this.updateTempo(now, events);
    else this.updateWait(now, events);
    return events;
  }

  input(e: InputEvent): SessionEvent[] {
    if (e.type === 'connection') {
      if (e.state === 'lost') return this.pause(e.time, e.detail ?? 'connexion perdue');
      if (e.state === 'restored') return this.resume(e.time);
      return [];
    }
    if (this.status === 'idle' || this.status === 'ended' || this.status === 'paused') return [];
    if (e.type === 'noteOff') return [];
    this.sources.add(e.source);
    if (e.type === 'onset') {
      this.counts.uncertainEvents++;
      const expectedPitch = this.options.mode === 'wait' ? this.firstPending() : this.nearestPendingPitch(e.time);
      if (this.options.mode === 'tempo') this.uncertainLog.push({ pitch: null, time: e.time });
      return [{ type: 'feedback', result: { category: 'uncertain', time: e.time, pitch: null, expectedPitch } }];
    }
    return this.options.mode === 'wait' ? this.inputWait(e.pitch, e.time, e.confidence === 'uncertain') : this.inputTempo(e.pitch, e.time, e.confidence === 'uncertain');
  }

  // --- Mode attente -------------------------------------------------------------

  private beginGlide(nextStep: number, now: number): void {
    this.anchorTick = this.tickAtInternal(now);
    this.anchorTime = now;
    if (nextStep >= this.steps.length) {
      this.targetStep = null;
      this.targetTick = Math.max(this.anchorTick, this.endTick);
      this.stepIndex = this.steps.length;
      this.pending = new Set();
    } else {
      this.targetStep = nextStep;
      this.targetTick = this.steps[nextStep].tick;
      this.stepIndex = nextStep;
      this.pending = new Set(this.steps[nextStep].notes.map((n) => n.pitch));
      this.stepHadError = false;
    }
    if (this.status === 'waiting') this.status = 'running';
  }

  private updateWait(now: number, events: SessionEvent[]): void {
    if (this.targetTick === null) return;
    if (this.tickAtInternal(now) < this.targetTick) return;
    // Arrivée sur la cible.
    this.anchorTick = this.targetTick;
    this.anchorTime = now;
    this.targetTick = null;
    if (this.targetStep === null) {
      events.push(...this.finish(now));
      return;
    }
    if (this.pending.size === 0) {
      // Pas déjà joué en avance : on enchaîne.
      this.beginGlide(this.targetStep + 1, now);
      return;
    }
    this.status = 'waiting';
    events.push({ type: 'status', status: 'waiting' });
    events.push({ type: 'step', stepIndex: this.stepIndex });
  }

  private firstPending(): number | undefined {
    let min: number | undefined;
    for (const p of this.pending) if (min === undefined || p < min) min = p;
    return min;
  }

  private inputWait(pitch: number, time: number, uncertain: boolean): SessionEvent[] {
    const now = Math.max(time, this.lastNow);
    const moving = this.targetTick !== null;
    const remainingMs = moving && this.targetTick !== null ? ticksToMs(this.targetTick - this.tickAtInternal(now), this.options.tempo, this.score.ppq) : 0;
    const expectedPitch = this.firstPending();

    if (uncertain) {
      this.counts.uncertainEvents++;
      return [{ type: 'feedback', result: { category: 'uncertain', time, pitch, expectedPitch } }];
    }
    if (this.targetStep === null && moving) {
      // Glissement final : plus rien à jouer.
      this.counts.extra++;
      return [{ type: 'feedback', result: { category: 'extra', time, pitch } }];
    }
    if (moving && remainingMs > this.options.earlyAcceptMs) {
      if (this.pending.has(pitch)) {
        return [{ type: 'feedback', result: { category: 'early', time, pitch, expectedPitch: pitch } }];
      }
      this.stepHadError = true;
      this.counts.wrong++;
      return [{ type: 'feedback', result: { category: 'wrong', time, pitch, expectedPitch } }];
    }
    if (this.pending.has(pitch)) {
      this.pending.delete(pitch);
      const step = this.steps[this.stepIndex];
      const note = step.notes.find((n) => n.pitch === pitch);
      const exp = note ? this.byId.get(note.id) : undefined;
      if (exp) {
        exp.status = 'hit';
        exp.category = 'correct';
      }
      const events: SessionEvent[] = [{ type: 'feedback', result: { category: 'correct', time, pitch, expectedPitch: pitch, noteId: note?.id } }];
      if (this.pending.size === 0) {
        if (!this.stepHadError) this.firstTrySteps++;
        if (!moving) {
          this.beginGlide(this.stepIndex + 1, now);
          events.push({ type: 'status', status: 'running' });
        }
      }
      return events;
    }
    for (const p of this.pending) {
      if (pitchClass(p) === pitchClass(pitch)) {
        this.stepHadError = true;
        this.counts.wrongOctave++;
        return [{ type: 'feedback', result: { category: 'wrongOctave', time, pitch, expectedPitch: p } }];
      }
    }
    this.stepHadError = true;
    this.counts.wrong++;
    return [{ type: 'feedback', result: { category: 'wrong', time, pitch, expectedPitch } }];
  }

  // --- Mode tempo ---------------------------------------------------------------

  private absoluteTime(e: ExpectedNote): number {
    return this.startTime + ticksToMs(this.leadInTicks, this.options.tempo, this.score.ppq) + e.offsetMs;
  }

  /** Une note attendue est finalisée (manquée ou non évaluable) une fois sa fenêtre passée, délai de réception compris. */
  private finalizeDelay(): number {
    return this.options.wideMs + this.options.inputDelayMs;
  }

  private updateTempo(now: number, events: SessionEvent[]): void {
    const tick = this.tickAtInternal(now);
    if (this.status === 'leadIn' && tick >= 0) {
      this.status = 'running';
      events.push({ type: 'status', status: 'running' });
    }
    for (const e of this.expected) {
      if (e.status !== 'pending') continue;
      const t = this.absoluteTime(e);
      if (t + this.finalizeDelay() < now) {
        const doubt = this.uncertainLog.some((u) => Math.abs(u.time - t) <= this.options.wideMs && (u.pitch === null || u.pitch === e.note.pitch));
        e.status = doubt ? 'uncertain' : 'missed';
        e.category = doubt ? 'uncertain' : 'missed';
        e.finalizedAt = now;
        events.push({ type: 'feedback', result: { category: e.category, time: now, pitch: null, expectedPitch: e.note.pitch, noteId: e.note.id } });
      }
    }
    if (now > this.timeOfTick(this.endTick) + this.finalizeDelay()) {
      events.push(...this.finish(now));
    }
  }

  private nearestPendingPitch(time: number): number | undefined {
    let best: ExpectedNote | undefined;
    let bestDelta = Infinity;
    for (const e of this.expected) {
      if (e.status !== 'pending') continue;
      const d = Math.abs(time - this.absoluteTime(e));
      if (d < bestDelta) {
        bestDelta = d;
        best = e;
      }
    }
    return best && bestDelta <= this.options.wideMs ? best.note.pitch : undefined;
  }

  /**
   * Notes attendues rattachables à une frappe : celles encore en attente dont la fenêtre
   * contient l'instant de la frappe, et celles déclarées manquées ou non évaluables depuis
   * peu (événement reçu avec retard, après qu'une minuterie a déjà conclu).
   */
  private candidatesFor(time: number): { e: ExpectedNote; delta: number }[] {
    const candidates: { e: ExpectedNote; delta: number }[] = [];
    for (const e of this.expected) {
      if (e.status === 'hit') continue;
      const delta = time - this.absoluteTime(e);
      if (Math.abs(delta) > this.options.wideMs) continue;
      if (e.status !== 'pending' && (e.finalizedAt === undefined || this.lastNow - e.finalizedAt > this.options.reconcileMs)) continue;
      candidates.push({ e, delta });
    }
    candidates.sort((a, b) => Math.abs(a.delta) - Math.abs(b.delta));
    return candidates;
  }

  private inputTempo(pitch: number, time: number, uncertain: boolean): SessionEvent[] {
    const candidates = this.candidatesFor(time);
    const nearest = candidates[0];

    if (uncertain) {
      this.counts.uncertainEvents++;
      this.uncertainLog.push({ pitch, time });
      const same = candidates.find((c) => c.e.note.pitch === pitch);
      return [{ type: 'feedback', result: { category: 'uncertain', time, pitch, expectedPitch: (same ?? nearest)?.e.note.pitch } }];
    }
    const same = candidates.find((c) => c.e.note.pitch === pitch);
    if (same) {
      const { e, delta } = same;
      const revised = e.status !== 'pending';
      const category: ResultCategory = Math.abs(delta) <= this.options.toleranceMs ? 'correct' : delta < 0 ? 'early' : 'late';
      e.status = 'hit';
      e.category = category;
      e.finalizedAt = undefined;
      return [{ type: 'feedback', result: { category, time, pitch, expectedPitch: pitch, noteId: e.note.id, deltaMs: Math.round(delta), ...(revised ? { revised: true } : {}) } }];
    }
    const octave = candidates.find((c) => pitchClass(c.e.note.pitch) === pitchClass(pitch));
    if (octave) {
      this.counts.wrongOctave++;
      return [{ type: 'feedback', result: { category: 'wrongOctave', time, pitch, expectedPitch: octave.e.note.pitch, deltaMs: Math.round(octave.delta) } }];
    }
    if (nearest) {
      this.counts.wrong++;
      return [{ type: 'feedback', result: { category: 'wrong', time, pitch, expectedPitch: nearest.e.note.pitch, deltaMs: Math.round(nearest.delta) } }];
    }
    this.counts.extra++;
    return [{ type: 'feedback', result: { category: 'extra', time, pitch } }];
  }

  // --- Commun -------------------------------------------------------------------

  private emitBeat(now: number, events: SessionEvent[]): void {
    const tick = this.tickAtInternal(now);
    const beatIndex = Math.floor(tick / this.tpb);
    if (beatIndex === this.lastBeatIndex) return;
    this.lastBeatIndex = beatIndex;
    const beatsPerMeasure = this.score.timeSignature.beats;
    const beat = ((beatIndex % beatsPerMeasure) + beatsPerMeasure) % beatsPerMeasure + 1;
    events.push({ type: 'beat', beat, measure: Math.floor(beatIndex / beatsPerMeasure), countIn: tick < 0, time: now });
  }

  private finish(now: number): SessionEvent[] {
    if (this.status === 'ended') return [];
    this.lastNow = now;
    if (this.options.mode === 'tempo') {
      for (const e of this.expected) {
        if (e.status === 'pending') {
          e.status = 'missed';
          e.category = 'missed';
          e.finalizedAt = now;
        }
      }
    }
    this.status = 'ended';
    this.summary = this.buildSummary();
    return [{ type: 'status', status: 'ended' }, { type: 'end', summary: this.summary }];
  }

  private buildSummary(): SessionSummary {
    const count = (c: ResultCategory): number => this.expected.filter((e) => e.category === c).length;
    const expected = this.expected.length;
    const correct = count('correct');
    const early = count('early');
    const late = count('late');
    const missed = count('missed');
    const unevaluated = count('uncertain');
    const evaluated = expected - unevaluated;
    const hits = correct + early + late;
    const faults = this.counts.wrong + this.counts.wrongOctave + this.counts.extra;
    const steps = this.steps.length;
    const uncertainEvents = this.counts.uncertainEvents;
    const precision = hits + faults === 0 ? 1 : hits / (hits + faults);
    let accuracyPitch: number;
    let accuracyTiming: number;
    let coverage: number;
    let quality: number;
    if (this.options.mode === 'wait') {
      accuracyPitch = steps === 0 ? 1 : this.firstTrySteps / steps;
      accuracyTiming = accuracyPitch;
      coverage = hits + uncertainEvents === 0 ? 1 : hits / (hits + uncertainEvents);
      quality = accuracyPitch;
    } else {
      accuracyPitch = evaluated === 0 ? 0 : hits / evaluated;
      accuracyTiming = evaluated === 0 ? 0 : correct / evaluated;
      coverage = expected === 0 ? 1 : evaluated / expected;
      quality = evaluated + faults === 0 ? 0 : hits / (evaluated + faults);
    }
    const sources = [...this.sources];
    const scoreHands = this.scoreHands;
    const hands = scoreHands.filter((h) => this.options.hands.includes(h));
    return {
      mode: this.options.mode,
      tempo: this.options.tempo,
      hands,
      fullHands: scoreHands.every((h) => hands.includes(h)),
      sources,
      instrument: sources.length > 0 && sources.every((s) => s === 'midi' || s === 'mic'),
      expected,
      evaluated,
      unevaluated,
      correct,
      early,
      late,
      hits,
      wrong: this.counts.wrong,
      wrongOctave: this.counts.wrongOctave,
      extra: this.counts.extra,
      missed,
      uncertainEvents,
      steps,
      firstTrySteps: this.firstTrySteps,
      accuracyPitch,
      accuracyTiming,
      precision,
      coverage,
      quality,
    };
  }
}
